/** 多图轮播 style.carouselExt 解析与默认值（与 docs/Carousel-component-json.md 一致） */

import Vue from "vue";

export const CAROUSEL_EXT_SCHEMA_VERSION = 1;

export const CAROUSEL_PLAY_MODES = ["legacy", "auto", "manual", "autoAndManual"];
export const CAROUSEL_STYLES = ["legacy", "fadeDots", "coverFlow", "arrows"];
export const CAROUSEL_LAYOUTS = ["horizontal", "vertical"];

const DEFAULT_FADE_DOTS = {
  dotsPosition: "bottomLeft",
  dotsClickable: true,
  pauseOnHover: true,
  showIndexBadge: true,
};

const DEFAULT_COVER_FLOW = {
  visibleCount: 3,
  centerBorderColor: "#FFB020",
  sideBorderColor: "#E02020",
  frameGlowColor: "#00D4FF",
  scaleCenter: 1,
  scaleSide: 0.72,
};

const DEFAULT_ARROWS = {
  position: "middleSides",
  size: 22,
  color: "rgba(120, 120, 120, 0.55)",
  showInAuto: false,
};

const DEFAULT_TRANSITION = {
  durationMs: 450,
  type: "fade",
};

export function ensureCarouselExt(style) {
  if (!style || typeof style !== "object") {
    return {};
  }
  if (!style.carouselExt || typeof style.carouselExt !== "object") {
    return {};
  }
  return style.carouselExt;
}

export function isLegacyCarousel(style) {
  const ext = ensureCarouselExt(style);
  if (!ext || Object.keys(ext).length === 0) {
    return true;
  }
  const play = ext.playMode || "legacy";
  const visual = ext.carouselStyle || "legacy";
  return play === "legacy" && visual === "legacy";
}

export function getCarouselPlayMode(style) {
  if (isLegacyCarousel(style)) {
    return "legacy";
  }
  const ext = ensureCarouselExt(style);
  const v = ext.playMode || "legacy";
  return CAROUSEL_PLAY_MODES.includes(v) ? v : "legacy";
}

export function getCarouselVisualStyle(style) {
  if (isLegacyCarousel(style)) {
    return "legacy";
  }
  const ext = ensureCarouselExt(style);
  const v = ext.carouselStyle || "legacy";
  return CAROUSEL_STYLES.includes(v) ? v : "legacy";
}

export function getCarouselLayout(style) {
  const ext = ensureCarouselExt(style);
  const v = (ext && ext.layoutDirection) || "horizontal";
  return CAROUSEL_LAYOUTS.includes(v) ? v : "horizontal";
}

export function shouldAutoplay(style, picCount) {
  if (picCount < 2) {
    return false;
  }
  const mode = getCarouselPlayMode(style);
  if (mode === "legacy" || mode === "auto" || mode === "autoAndManual") {
    return true;
  }
  return false;
}

export function mergeFadeDots(style) {
  const ext = ensureCarouselExt(style);
  return { ...DEFAULT_FADE_DOTS, ...(ext.fadeDots || {}) };
}

export function mergeCoverFlow(style) {
  const ext = ensureCarouselExt(style);
  return { ...DEFAULT_COVER_FLOW, ...(ext.coverFlow || {}) };
}

export function mergeArrows(style) {
  const ext = ensureCarouselExt(style);
  return { ...DEFAULT_ARROWS, ...(ext.arrows || {}) };
}

export function mergeTransition(style) {
  const ext = ensureCarouselExt(style);
  const t = { ...DEFAULT_TRANSITION, ...(ext.transition || {}) };
  const legacy = isLegacyCarousel(style);
  return {
    durationMs: legacy ? 450 : Number(t.durationMs) || 450,
    type: legacy ? "fade" : t.type || "fade",
  };
}

/** 编辑器：写入 carouselExt 前确保对象存在 */
export function initCarouselExtOnStyle(style) {
  if (!style || typeof style !== "object") {
    return;
  }
  if (!style.carouselExt || typeof style.carouselExt !== "object") {
    Vue.set(style, "carouselExt", { schemaVersion: CAROUSEL_EXT_SCHEMA_VERSION });
  }
  if (style.carouselExt.schemaVersion == null) {
    Vue.set(style.carouselExt, "schemaVersion", CAROUSEL_EXT_SCHEMA_VERSION);
  }
}

export const CAROUSEL_EXT_ATTR_MAP = {
  carouselPlayMode: "playMode",
  carouselVisualStyle: "carouselStyle",
  carouselLayoutDirection: "layoutDirection",
};

export function getExtFieldByAttrKey(style, attrKey) {
  const path = CAROUSEL_EXT_ATTR_MAP[attrKey];
  if (!path) {
    return "";
  }
  const ext = ensureCarouselExt(style);
  if (!ext || Object.keys(ext).length === 0) {
    return "legacy";
  }
  if (path === "layoutDirection") {
    return ext.layoutDirection || "horizontal";
  }
  return ext[path] || "legacy";
}

export function setExtFieldByAttrKey(style, attrKey, val) {
  initCarouselExtOnStyle(style);
  const path = CAROUSEL_EXT_ATTR_MAP[attrKey];
  if (path) {
    Vue.set(style.carouselExt, path, val);
  }
}

/** 画布组件 :key，样式变更时强制重挂载以便立即切换 UI */
export function carouselEditorRenderKey(item) {
  if (!item) {
    return "cmp-unknown";
  }
  if (item.component !== "Carousel") {
    return String(item.id != null ? item.id : item.component || "cmp");
  }
  const ext = item.style && item.style.carouselExt;
  const baseId = item.id != null ? String(item.id) : "carousel";
  if (!ext) {
    return baseId;
  }
  return [
    baseId,
    ext.carouselStyle || "legacy",
    ext.playMode || "legacy",
    ext.layoutDirection || "horizontal",
  ].join("-");
}

export function clearCarouselExtOnStyle(style) {
  if (!style || !style.carouselExt) {
    return;
  }
  Vue.delete(style, "carouselExt");
}
